import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Package, 
  Boxes, 
  DollarSign, 
  TrendingUp, 
  FileText,
  ShoppingCart,
  Building2,
  BarChart3,
  Settings
} from 'lucide-react';

export function EnterpriseNavigation({ 
  activeTab, 
  onTabChange 
}) {
  const { role } = useAuth();
  const { hasAccess } = usePermission();
  
  // Define all navigation items with their permission modules
  const allNavItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard, 
      description: 'Executive KPIs and insights',
      module: 'dashboard'
    },
    { 
      id: 'sales', 
      label: 'Sales', 
      icon: DollarSign, 
      description: 'Revenue, costs, and profit analysis',
      module: 'sales'
    },
    { 
      id: 'inventory', 
      label: 'Inventory', 
      icon: Package, 
      description: 'Stock levels across all locations',
      module: 'inventory'
    },
    { 
      id: 'purchase-orders', 
      label: 'Purchases', 
      icon: ShoppingCart, 
      description: 'Purchase orders and supplier management',
      module: 'purchase_orders'
    },
    { 
      id: 'finance', 
      label: 'Finance', 
      icon: Building2, 
      description: 'Overhead allocation and employee costs',
      module: 'finance'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'Team and company settings',
      module: 'settings'
    }
  ];
  
  // Filter navigation items based on user permissions
  const navItems = allNavItems.filter(item => {
    // Dashboard is always visible
    if (item.id === 'dashboard') return true;
    
    // Check if user has access to the module
    const hasModuleAccess = hasAccess(item.module);
    
    // Debug logging for finance
    if (item.module === 'finance') {
      console.log('🔍 Navigation filtering for finance:', {
        itemId: item.id,
        module: item.module,
        hasAccess: hasModuleAccess
      });
    }
    
    return hasModuleAccess;
  });
  
  return (
    <nav className="border-b border-neutral-200 dark:border-neutral-700
                  bg-white dark:bg-neutral-900">
      <div className="px-6">
        <div className="flex items-center gap-0.5">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            const Icon = item.icon;
            
            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={`
                  relative flex items-center gap-2 px-3 py-2.5 group
                  text-xs font-medium transition-all duration-200
                  ${isActive
                    ? 'text-enterprise-primary-600 dark:text-enterprise-primary-400'
                    : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                  }
                `}
                title={item.description}
              >
                <Icon className={`w-4 h-4 transition-transform duration-200 ${
                  isActive ? 'scale-110' : 'group-hover:scale-105'
                }`} />
                <span className="font-medium">{item.label}</span>
                
                {/* Active Indicator with Animation */}
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5
                             bg-enterprise-primary-600 dark:bg-enterprise-primary-400 rounded-full"
                    transition={{ 
                      type: "spring", 
                      stiffness: 500, 
                      damping: 30,
                      duration: 0.3 
                    }}
                  />
                )}
                
                {/* Hover Effect */}
                {!isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5
                               bg-neutral-300 dark:bg-neutral-600 rounded-full
                               opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

export default EnterpriseNavigation;